import { lazy, Suspense } from 'react';

import { Card, CardContent } from '@/components/ui/card';

import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useQrMatrix } from '../hooks/useQrMatrix';
import { QrProvider } from '../state/QrProvider';
import { useQrContext } from '../state/qr-context';
import { BasicSettings } from './BasicSettings';
import { CustomizationTabs } from './CustomizationTabs';
import { HistoryToolbar } from './HistoryToolbar';
import { PreviewPanel } from './PreviewPanel';

/**
 * The scanner pulls in jsQR (~250 kB unminified) but is only reached when the
 * user actually wants to verify a code, so it is split into its own chunk.
 */
const ScannerCard = lazy(() =>
  import('@/features/scanner/ScannerCard').then((module) => ({ default: module.ScannerCard })),
);

function ScannerFallback() {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">
        Scanner wird geladen…
      </CardContent>
    </Card>
  );
}

function CustomizerLayout() {
  const { design, actions } = useQrContext();
  useKeyboardShortcuts();

  // Generated once here and shared, so the settings form and the preview can
  // never disagree about whether the current content is encodable.
  const result = useQrMatrix(design.content, design.errorCorrectionLevel);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <HistoryToolbar />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <BasicSettings result={result} />
          <CustomizationTabs />
        </div>

        <div className="space-y-6">
          <PreviewPanel result={result} />
          <Suspense fallback={<ScannerFallback />}>
            <ScannerCard onDecoded={actions.setContent} />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export function QrCodeCustomizer() {
  return (
    <QrProvider>
      <CustomizerLayout />
    </QrProvider>
  );
}
